import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { opportunityService } from '../services/api';

function formatUSD(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + Number(n).toLocaleString();
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className={`kpi-card${accent ? ' kpi-' + accent : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export default function PartnerDashboard() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    opportunityService.getAll()
      .then(d => setOpportunities(d.opportunities || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const kpis = useMemo(() => {
    const open = opportunities.filter(o => o.deal_stage !== 'Launched');
    const won = opportunities.filter(o => o.deal_stage === 'Launched');
    const totalARR = open.reduce((s, o) => s + (parseFloat(o.arr_usd) || 0), 0);
    const wonARR = won.reduce((s, o) => s + (parseFloat(o.arr_usd) || 0), 0);
    const cumulativeRevenue = opportunities.reduce((s, o) => {
      const arr = parseFloat(o.arr_usd) || 0;
      const pct = parseFloat(o.revenue_share_pct) || 0;
      return s + (arr * pct / 100);
    }, 0);
    return { total: opportunities.length, open: open.length, won: won.length, totalARR, wonARR, cumulativeRevenue };
  }, [opportunities]);

  const byStage = useMemo(() => {
    const map = {};
    opportunities.forEach(o => {
      const s = o.deal_stage || 'New';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [opportunities]);

  const recent = useMemo(
    () => [...opportunities].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5),
    [opportunities]
  );

  if (loading) return <div className="loading">Loading dashboard…</div>;

  return (
    <div className="partner-dashboard">
      <div className="dashboard-header">
        <div>
          <h2>My Dashboard</h2>
          <span className="dashboard-subtitle">Your opportunity pipeline at a glance</span>
        </div>
        <Link to="/submit" className="btn-primary ck-btn-primary">+ Submit Opportunity</Link>
      </div>

      <div className="kpi-row">
        <KPI label="Total Opportunities" value={kpis.total} />
        <KPI label="Active Pipeline" value={kpis.open} sub="Open deals" />
        <KPI label="Pipeline ARR" value={formatUSD(kpis.totalARR)} sub="Open deals" accent="blue" />
        <KPI label="Launched" value={kpis.won} accent="green" sub={formatUSD(kpis.wonARR)} />
        <KPI label="Cumulative Revenue" value={formatUSD(kpis.cumulativeRevenue)} sub="Based on rev share %" accent="mint" />
      </div>

      <div className="dash-two-col">
        <div className="dash-panel">
          <h3 className="dash-panel-title">Pipeline by Stage</h3>
          {byStage.length === 0 ? (
            <p className="empty-note">No opportunities yet.</p>
          ) : (
            <div className="stage-bar-list">
              {byStage.map(([stage, count]) => {
                const pct = Math.round((count / kpis.total) * 100);
                return (
                  <div key={stage} className="stage-bar-row">
                    <span className="stage-bar-label">{stage}</span>
                    <div className="stage-bar-track">
                      <div className="stage-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="stage-bar-count">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dash-panel">
          <div className="dash-panel-head">
            <h3 className="dash-panel-title">Recent Submissions</h3>
            <Link to="/opportunities" className="dash-panel-link">View all →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="empty-state" style={{ padding: '1.5rem 0' }}>
              <p>No opportunities yet.</p>
              <Link to="/submit" className="btn-primary ck-btn-primary">Submit your first</Link>
            </div>
          ) : (
            <div className="recent-list">
              {recent.map(opp => (
                <Link key={opp.opportunity_id} to={`/opportunities/${opp.opportunity_id}`} className="recent-item">
                  <div className="recent-item-main">
                    {opp.cpn_id && <span className="cpn-id">{opp.cpn_id}</span>}
                    <span className="recent-item-name">{opp.opportunity_name}</span>
                    <span className="recent-item-company">{opp.company_name}</span>
                  </div>
                  <div className="recent-item-meta">
                    <span className="recent-item-arr">{formatUSD(opp.arr_usd)}</span>
                    <span className={`badge badge-${opp.status}`}>{(opp.status || '').replace('_', ' ')}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
